import ServiceClass from './ServiceClass';
import ClientConfig from '../config';

/**
 * The chat service is the one place that does not go through the axios wrapper
 * in ServiceClass: axios cannot stream a response body in the browser, and
 * EventSource cannot send a POST with an Authorization header. So the streaming
 * call uses native fetch and reads the SSE frames off the ReadableStream itself.
 *
 * The non-streaming endpoints keep the normal ServiceClass shape.
 */
class ChatServiceImpl extends ServiceClass {
    async getStatus(token) {
        return await this.get('/api/chat/status', { Authorization: `Bearer ${token}` });
    }

    async getThread(token) {
        return await this.get('/api/chat/thread', { Authorization: `Bearer ${token}` });
    }

    async startNewThread(token) {
        return await this.post('/api/chat/thread/new', {}, { Authorization: `Bearer ${token}` });
    }

    /**
     * Streams one turn. `onEvent(name, data)` fires per SSE frame.
     * Resolves when the stream closes; rejects on a non-200 response.
     */
    async streamMessage({ token, threadId, message, signal, onEvent }) {
        const response = await fetch(`${ClientConfig.apiBaseHost}/api/chat/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ threadId, message }),
            signal,
        });

        if (!response.ok) {
            // Errors raised before the stream opens come back as ordinary JSON.
            const body = await response.json().catch(() => ({}));
            const error = new Error(body.message || 'chat request failed');
            error.code = body.code;
            error.status = response.status;
            throw error;
        }

        // A serverless function killed at its time limit closes the socket with no
        // final frame. Without this the UI would stream nothing and spin forever.
        let sawTerminalEvent = false;

        const reader = response.body.getReader();
        // One decoder for the whole stream: a Hebrew character can straddle two
        // chunks, and { stream: true } is what stops it being mangled.
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            // Frames are separated by a blank line; the trailing part may be partial.
            const frames = buffer.split('\n\n');
            buffer = frames.pop();

            for (const frame of frames) {
                if (!frame.trim() || frame.startsWith(':')) continue; // heartbeat

                const name = frame.match(/^event: (.+)$/m)?.[1];
                const rawData = frame.match(/^data: (.+)$/m)?.[1];
                if (!name) continue;

                let data = {};
                try {
                    data = rawData ? JSON.parse(rawData) : {};
                } catch {
                    continue;
                }

                if (name === 'done' || name === 'error') sawTerminalEvent = true;
                onEvent(name, data);
            }
        }

        if (!sawTerminalEvent) {
            const error = new Error('stream ended before the answer completed');
            error.code = 'AI_STREAM_TRUNCATED';
            throw error;
        }
    }
}

const chatService = new ChatServiceImpl(ClientConfig.apiBaseHost, '');
export default chatService;
